import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Clock, Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { MessageAttachmentCleanupResult, MessageAttachmentCleanupStatus } from "./types";
import { MESSAGE_ATTACHMENT_FAILURE_THRESHOLD } from "./constants";
import { formatRelativeTime } from "./helpers";

export function MessageAttachmentStoragePanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const statusQuery = useQuery<MessageAttachmentCleanupStatus>({
    queryKey: ["/api/admin/message-attachments/cleanup-status"],
    queryFn: async () => {
      const res = await fetch("/api/admin/message-attachments/cleanup-status", {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load cleanup status");
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const runMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/message-attachments/cleanup", {});
      return (await res.json()) as MessageAttachmentCleanupResult;
    },
    onSuccess: (result) => {
      toast({
        title: "Cleanup ran",
        description:
          `Scanned ${result.scanned}, deleted ${result.deleted}, ` +
          `retained ${result.retained}, errors ${result.errors}.`,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/message-attachments/cleanup-status"],
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Cleanup failed",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  const status = statusQuery.data;
  const lastRun = status?.lastRun ?? null;
  const result = lastRun?.result ?? null;
  const errors = result?.errors ?? 0;
  const failureFlagged = errors >= MESSAGE_ATTACHMENT_FAILURE_THRESHOLD;
  const lastCronOk = !!status?.lastSuccessfulCronAt;
  const cronAgeMs = status?.lastSuccessfulCronAt
    ? Date.now() - Date.parse(status.lastSuccessfulCronAt)
    : null;
  // Cron runs daily at 4:45 AM. If we haven't seen a successful run in
  // more than 36 hours something is wrong — flag it.
  const cronStuck = cronAgeMs !== null && cronAgeMs > 36 * 60 * 60 * 1000;

  return (
    <div className="space-y-4" data-testid="panel-message-attachments">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-message-attachments-title">
            Message Attachment Storage Health
          </h3>
          <p className="text-sm text-muted-foreground">
            Tracks the daily janitor (4:45 AM cron) that deletes orphaned
            message-attachment uploads from object storage. Spikes in
            failures usually indicate a GCS permission problem.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => runMutation.mutate()}
          disabled={runMutation.isPending}
          data-testid="button-run-message-attachment-cleanup"
        >
          {runMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Run cleanup now
        </Button>
      </div>

      {statusQuery.isLoading ? (
        <Card className="p-6">
          <Skeleton className="h-20 w-full" />
        </Card>
      ) : statusQuery.error ? (
        <Card className="p-6" data-testid="card-message-attachments-error">
          <p className="text-sm text-destructive">
            Failed to load: {parseErrorMessage(statusQuery.error as Error, "Unknown error")}
          </p>
        </Card>
      ) : !lastRun ? (
        <Card className="p-6" data-testid="card-message-attachments-empty">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            No cleanup run recorded yet on this server. The next cron is
            scheduled for 4:45 AM, or you can trigger one manually.
          </div>
        </Card>
      ) : (
        <>
          <Card className="p-6" data-testid="card-message-attachments-summary">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {!failureFlagged ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <ShieldAlert className="h-5 w-5 text-red-600" />
              )}
              <span className="font-semibold" data-testid="text-message-attachments-headline">
                {failureFlagged
                  ? `${errors} error(s) in the last run`
                  : "Last run completed cleanly"}
              </span>
              <Badge
                variant="outline"
                data-testid={`badge-message-attachments-source-${lastRun.source}`}
              >
                Triggered by {lastRun.source === "cron" ? "cron" : "admin"}
              </Badge>
              <span
                className="text-xs text-muted-foreground"
                data-testid="text-message-attachments-finished-at"
              >
                {formatRelativeTime(lastRun.finishedAt)} ({lastRun.finishedAt})
              </span>
            </div>

            {result && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-md border p-3" data-testid="stat-scanned">
                  <p className="text-xs text-muted-foreground">Scanned</p>
                  <p className="text-2xl font-semibold tabular-nums">{result.scanned}</p>
                </div>
                <div className="rounded-md border p-3" data-testid="stat-deleted">
                  <p className="text-xs text-muted-foreground">Deleted</p>
                  <p className="text-2xl font-semibold tabular-nums">{result.deleted}</p>
                </div>
                <div className="rounded-md border p-3" data-testid="stat-retained">
                  <p className="text-xs text-muted-foreground">Retained</p>
                  <p className="text-2xl font-semibold tabular-nums">{result.retained}</p>
                </div>
                <div
                  className={
                    failureFlagged
                      ? "rounded-md border border-red-400 bg-red-50 p-3 dark:border-red-700 dark:bg-red-950"
                      : "rounded-md border p-3"
                  }
                  data-testid="stat-errors"
                >
                  <p className="text-xs text-muted-foreground">Errors</p>
                  <p
                    className={
                      failureFlagged
                        ? "text-2xl font-semibold tabular-nums text-red-700 dark:text-red-300"
                        : "text-2xl font-semibold tabular-nums"
                    }
                  >
                    {result.errors}
                  </p>
                </div>
              </div>
            )}
          </Card>

          <Card className="p-6" data-testid="card-message-attachments-cron">
            <div className="flex flex-wrap items-center gap-3">
              {cronStuck ? (
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              ) : lastCronOk ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <Clock className="h-5 w-5 text-muted-foreground" />
              )}
              <div>
                <p className="text-sm font-medium">Last successful cron run</p>
                <p
                  className="text-sm text-muted-foreground"
                  data-testid="text-message-attachments-last-cron"
                >
                  {status?.lastSuccessfulCronAt
                    ? `${formatRelativeTime(status.lastSuccessfulCronAt)} (${status.lastSuccessfulCronAt})`
                    : "No successful cron run since this server started."}
                </p>
              </div>
              {cronStuck && (
                <Badge
                  variant="outline"
                  className="border-yellow-400 bg-yellow-50 text-yellow-800 dark:border-yellow-700 dark:bg-yellow-950 dark:text-yellow-200"
                  data-testid="badge-message-attachments-cron-stuck"
                >
                  Stuck?
                </Badge>
              )}
            </div>
          </Card>

        </>
      )}
    </div>
  );
}
